// Token Expiration Handler
// Maneja la expiración de tokens JWT y notifica al usuario

class TokenExpirationHandler {
  constructor() {
    this.expirationWarningTime = 30 * 60 * 1000; // 30 minutos antes de expirar
    this.checkInterval = 60 * 1000; // Verificar cada minuto
    this.warningShown = false;
    this.intervalId = null;
    this.onExpirationCallback = null;
    this.onWarningCallback = null;
  }

  // Iniciar el monitoreo del token
  startMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      this.checkTokenExpiration();
    }, this.checkInterval);

    // Verificar inmediatamente
    this.checkTokenExpiration();
  }

  // Detener el monitoreo
  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.warningShown = false;
  }

  // Verificar si el token está próximo a expirar
  checkTokenExpiration() {
    const token = localStorage.getItem('token');
    if (!token) {
      this.handleTokenExpired();
      return;
    }

    try {
      // Decodificar el token JWT
      const payload = this.decodeJWT(token);
      if (!payload || !payload.exp) {
        this.handleTokenExpired();
        return;
      }

      const now = Date.now();
      const expirationTime = payload.exp * 1000; // Convertir a milisegundos
      const timeUntilExpiration = expirationTime - now;

      // Si el token ya expiró
      if (timeUntilExpiration <= 0) {
        this.handleTokenExpired();
        return;
      }

      // Si está próximo a expirar y no se ha mostrado la advertencia
      if (timeUntilExpiration <= this.expirationWarningTime && !this.warningShown) {
        this.handleTokenExpiringSoon(timeUntilExpiration);
      }
    } catch (error) {
      console.error('Error verificando token:', error);
      this.handleTokenExpired();
    }
  }

  // Decodificar JWT (solo para obtener la fecha de expiración)
  decodeJWT(token) {
    try {
      // Remover "Bearer " si está presente
      const cleanToken = token.replace('Bearer ', '');
      const parts = cleanToken.split('.');
      if (parts.length !== 3) {
        throw new Error('Token inválido');
      }
      
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      console.error('Error decodificando token:', error);
      return null;
    }
  }

  // Manejar cuando el token está próximo a expirar
  handleTokenExpiringSoon(timeUntilExpiration) {
    this.warningShown = true;
    const minutesLeft = Math.ceil(timeUntilExpiration / (60 * 1000));
    
    if (this.onWarningCallback) {
      this.onWarningCallback(minutesLeft);
    }
  }

  // Manejar cuando el token ha expirado
  handleTokenExpired() {
    this.stopMonitoring();
    localStorage.removeItem('token');
    
    if (this.onExpirationCallback) {
      this.onExpirationCallback();
    }
  }

  // Configurar callbacks
  setOnExpirationCallback(callback) {
    this.onExpirationCallback = callback;
  }

  setOnWarningCallback(callback) {
    this.onWarningCallback = callback;
  }

  // Verificar si hay un token válido
  isTokenValid() {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const payload = this.decodeJWT(token);
      if (!payload || !payload.exp) return false;

      const now = Date.now();
      const expirationTime = payload.exp * 1000;
      return expirationTime > now;
    } catch (error) {
      return false;
    }
  }

  // Obtener tiempo restante del token en minutos
  getTimeRemaining() {
    const token = localStorage.getItem('token');
    if (!token) return 0;

    try {
      const payload = this.decodeJWT(token);
      if (!payload || !payload.exp) return 0;

      const now = Date.now();
      const expirationTime = payload.exp * 1000;
      const timeUntilExpiration = expirationTime - now;

      return Math.max(0, Math.ceil(timeUntilExpiration / (60 * 1000)));
    } catch (error) {
      return 0;
    }
  }
}

// Instancia singleton
const tokenExpirationHandler = new TokenExpirationHandler();

export default tokenExpirationHandler;
